import Vue from 'vue';
import EventEmitter from 'events';
import { onLogin, onLogout, apolloProvider, AUTH_TOKEN } from '@/graphql/vueApollo'; // GraphQL connection
import AppState from '@/store/AppState';
import User from '@/store/User';
import Role from '@/store/Role';
import { wipeData } from '@/store/plugin';
import formatGraphQlError from '@/utilities/formatGraphQlError';
import logger from 'client-logger';

// Our GraphQL queries/mutations
import LOGIN from '@/graphql/mutations/login.gql';
import REFRESH_TOKEN from '@/graphql/mutations/refreshToken.gql';

const apollo = apolloProvider.defaultClient;

// Data constants (labels)
const TOKEN_EXPIRY = 'tokenExpiry';
const LOGIN_EVENT = 'loginEvent';

// A class that allows us to more easily handle auth events and pipe them elsewhere
class AuthService extends EventEmitter {
	constructor() {
		super(); // Get that EventEmitter goodness

		this.token = null;
		this.tokenExpiry = null;
		this.expiryTimer = null;
		this.loggedIn = false;

		// Bind our class methods
		this.login = this.login.bind(this);
		this.renewToken = this.renewToken.bind(this);
		this.localLogin = this.localLogin.bind(this);
		this.logout = this.logout.bind(this);
	}

	// Logs the user in, retrieves a token, and sets the app in motion
	async login(email, password) {
		// Get a user token from our server with these creds
		let user;
		try {
			const response = await apollo.mutate({
				mutation: LOGIN,
				variables: {
					email,
					password
				}
			});

			Vue.set(this, 'token', response.data.login.token);
			Vue.set(this, 'tokenExpiry', response.data.login.tokenExpiry);
			user = response.data.login.user;
		} catch (error) {
			const formattedError = formatGraphQlError(error);
			logger.warn({ email, error: formattedError }, 'Error trying to authenticate user');

			// Returns a nicely formatted GraphQL error object. Cheers!
			return formattedError;
		}

		return await this.localLogin(user);
	}

	// Will attempt to renew our logged-in state if possible, or reject if not
	async renewToken() {
		this.tokenExpiry = localStorage.getItem(TOKEN_EXPIRY);
		if (!this.tokenExpiry || Date.now() > this.tokenExpiry) {
			return 'Not logged in';
		} else {
			// Refresh our token
			const token = localStorage.getItem(AUTH_TOKEN);
			let user;
			try {
				const response = await apollo.mutate({
					mutation: REFRESH_TOKEN,
					variables: {
						token
					}
				});

				// Now we got all our data fresh! Hooray!
				Vue.set(this, 'token', response.data.refreshToken.token);
				Vue.set(this, 'tokenExpiry', response.data.refreshToken.tokenExpiry);
				user = response.data.refreshToken.user;
			} catch (error) {
				const formattedError = formatGraphQlError(error);
				logger.error({ error: formattedError }, 'Error trying to refresh token');

				// Returns a nicely formatted GraphQL error object. Cheers!
				return formattedError;
			}

			return await this.localLogin(user);
		}
	}

	// Initializes our app's authentication and user state
	async localLogin(user) {
		Vue.set(this, 'loggedIn', true);

		// Save our token expiry (for checking between refreshes)
		localStorage.setItem(TOKEN_EXPIRY, this.tokenExpiry);

		// Upload the token to our GraphQL connection headers
		await onLogin(this.token);

		// Now update our store with the user object we got back (and fetch their permissions)
		User.setCurrentUser(user);
		Role.initializeServerPermissions(user);

		// Notify components and others of this success
		this.emit(LOGIN_EVENT, {
			loggedIn: true,
			user
		});

		// Set a timer to allow us to notify the user of impending logout
		const warnMillis = this.tokenExpiry - Date.now() - 60 * 60 * 1000;
		setTimeout(
			() => {
				// Throw up the warning modal
				AppState.showModalMsg('Your session will expire very soon. If you wish to continue, we recommend you refresh the page.', 'FYI');
			},
			warnMillis > 0 ? warnMillis : 0
		); // Warn them with an hour to go

		// Set a timer to notify them that they have been logged out
		this.expiryTimer = setTimeout(() => {
			// Throw up the notification modal - they will be logged out after they close the modal
			AppState.showModalMsg('Your session has expired. Please login again to continue.', 'Session Expired', this.logout);
		}, this.tokenExpiry - Date.now());
	}

	// Easy - logs out. Includes wiping of all session info in the browser.
	async logout() {
		localStorage.removeItem(TOKEN_EXPIRY);

		Vue.set(this, 'loggedIn', false);
		Vue.set(this, 'token', null);
		Vue.set(this, 'tokenExpiry', null);

		// Clear the expiry timer, if set
		if (this.expiryTimer) {
			clearTimeout(this.expiryTimer);
		}

		// Wipe our connection auth headers
		await onLogout();

		// Wipe our store data
		wipeData();

		this.emit(LOGIN_EVENT, { loggedIn: false });
	}
}

export default new AuthService();
